import { useCallback } from 'react';
import { css, cx } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  PaymentMethodSetupEventHandler,
  PaymentMethodSetupModal,
  SavedPaymentMethod,
  useCompletePaymentMethodSetupRedirect,
  usePreparePaymentMethodSetup,
} from '@frontend/payments-platform';
import {
  Button,
  InlineNotification,
  NakedButton,
  SpinningLoader,
  Text,
  useModalControl,
} from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../../helpers';
import type { AchOnFileValue, CardOnFileValue } from '../../../../../../types/form-field-state.types';
import { useFillingExperienceContext } from '../../../../provider';
import type { DynamicFormApi } from '../../hooks/use-app-form';
import type { DynamicFormField } from '../../types';
import { MaskedSummary } from './masked-summary';
import {
  hasSavedPaymentMethod,
  isPaymentValueSatisfied,
  PAYMENT_KIND,
  PaymentFieldType,
  toFieldValue,
} from './payment-field.helpers';
import { PaymentPreviewModal } from './payment-preview-modal';
import { trackPaymentFieldEvent } from './payment-telemetry';
import { useFormsPaymentMethodSetupApi, usePaymentContext, usePaymentGatewayConfig } from './use-payment-api';

export interface PaymentOnFileFieldProps {
  form: DynamicFormApi;
  fieldInfo: DynamicFormField;
  type: PaymentFieldType;
}

export const PaymentOnFileField = ({ form, fieldInfo, type }: PaymentOnFileFieldProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const context = useFillingExperienceContext();
  const isPreviewMode = 'isPreviewMode' in context && !!context.isPreviewMode;
  const required = !!fieldInfo.required;
  const requiredMessage =
    type === 'cardOnFile'
      ? t('Please add your card details to proceed further')
      : t('Please add your ACH details to proceed further');

  const validate = (value: unknown) => {
    // Previews never change the value, so a required field could never pass.
    if (fieldInfo.hidden || !required || isPreviewMode) return undefined;
    return isPaymentValueSatisfied(type, value) ? undefined : requiredMessage;
  };

  return (
    <form.AppField
      name={fieldInfo.key}
      validators={{ onChange: ({ value }) => validate(value), onSubmit: ({ value }) => validate(value) }}
    >
      {(field) => {
        const errors = field.state.meta.isTouched ? (field.state.meta.errors as string[]) : [];
        return (
          <PaymentOnFileInput
            type={type}
            fieldInfo={fieldInfo}
            value={field.state.value}
            errors={errors.filter(Boolean)}
            onChange={(value) => field.handleChange(value)}
          />
        );
      }}
    </form.AppField>
  );
};

interface PaymentOnFileInputProps {
  type: PaymentFieldType;
  fieldInfo: DynamicFormField;
  value: unknown;
  errors: string[];
  onChange: (value: CardOnFileValue | AchOnFileValue) => void;
}

const PaymentOnFileInput = ({ type, fieldInfo, value, errors, onChange }: PaymentOnFileInputProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const context = useFillingExperienceContext();
  const isPreviewMode = 'isPreviewMode' in context && !!context.isPreviewMode;
  const paymentContext = usePaymentContext();

  const { modalProps, openModal, closeModal, triggerProps } = useModalControl();
  const api = useFormsPaymentMethodSetupApi(paymentContext);
  const gatewayConfig = usePaymentGatewayConfig();
  const kind = PAYMENT_KIND[type];

  const handleSaved = useCallback((method: SavedPaymentMethod) => onChange(toFieldValue(method)), [onChange]);
  const handleEvent = useCallback<PaymentMethodSetupEventHandler>(
    (event, data) =>
      trackPaymentFieldEvent(
        event,
        type,
        data,
        event === 'save_error' || event === 'redirect_error' || event === 'setup_session_failed' ? 'warning' : 'info'
      ),
    [type]
  );

  // Opens the modal only once the session and processor SDK are ready; the tile shows the wait.
  const setup = usePreparePaymentMethodSetup({
    kind,
    api,
    gatewayConfig,
    enabled: !isPreviewMode,
    onEvent: handleEvent,
  });

  const { redirectStatus } = useCompletePaymentMethodSetupRedirect({
    kind,
    setupKey: fieldInfo.key,
    enabled: !isPreviewMode && !!paymentContext,
    api,
    gatewayConfig,
    onComplete: handleSaved,
    onEvent: handleEvent,
  });

  const isCard = type === 'cardOnFile';
  const isLoadingDetails = redirectStatus === 'inProgress';
  const hasSaved = hasSavedPaymentMethod(type, value);
  const labelId = `${fieldInfo.key}-label`;
  const hasError = errors.length > 0;

  const handleOpen = async () => {
    if (isPreviewMode) {
      openModal();
      return;
    }
    if (!(await setup.prepare())) return;
    trackPaymentFieldEvent('modal_opened', type, { replace: hasSaved });
    openModal();
  };

  const handleClose = () => {
    setup.reset();
    closeModal();
  };

  // Draft restore failures surface through the form-level partial-restore notice; only a redirect completes here.
  const downloadError =
    redirectStatus === 'error'
      ? isCard
        ? t('Unable to load last saved card details!')
        : t('Unable to load last saved ACH details!')
      : '';
  const setupError = setup.hasError ? t("We couldn't start secure payment setup. Please try again.") : '';
  const inlineErrors = [downloadError, setupError, ...errors].filter(Boolean);

  return (
    <fieldset
      id={fieldInfo.key}
      tabIndex={-1}
      className={fieldsetStyle}
      role='group'
      aria-required={fieldInfo.required}
      aria-invalid={hasError}
      aria-labelledby={labelId}
      aria-describedby={inlineErrors.length ? `${fieldInfo.key}-error` : undefined}
    >
      <Text id={labelId} weight='bold' className={labelStyle}>
        {formatFieldLabel(fieldInfo.label, fieldInfo.required)}
      </Text>

      {isLoadingDetails ? (
        <div className={loadingRowStyle} role='status'>
          <SpinningLoader size='xs' />
          <Text size='small' color='subdued'>
            {isCard ? t('Loading card details...') : t('Loading ACH details...')}
          </Text>
        </div>
      ) : hasSaved ? (
        <div className={savedStyle}>
          <div className={savedRowStyle}>
            <MaskedSummary type={type} value={value} />
            {!isPreviewMode && (
              <Button
                {...triggerProps}
                variant='secondary'
                size='small'
                onClick={handleOpen}
                loading={setup.isPreparing}
                disabled={setup.isPreparing}
              >
                {t('Replace')}
              </Button>
            )}
          </div>
          <InlineNotification status='success'>
            {isCard
              ? t(
                  'Your card details have been submitted successfully. Please proceed to fill and submit the rest of your form.'
                )
              : t(
                  'Your ACH details have been submitted successfully. Please proceed to fill and submit the rest of your form.'
                )}
          </InlineNotification>
        </div>
      ) : (
        <>
          <NakedButton
            {...triggerProps}
            type='button'
            className={cx(addTileStyle, hasError && addTileErrorStyle)}
            onClick={handleOpen}
            disabled={setup.isPreparing}
            aria-busy={setup.isPreparing}
          >
            {setup.isPreparing ? <SpinningLoader size='xs' /> : <Icon name='plus' size={20} />}
            <Text as='span' weight='bold'>
              {isCard ? t('Add Card on File') : t('Add ACH on File')}
            </Text>
          </NakedButton>
          {isPreviewMode && (
            <Text size='small' color='subdued'>
              {isCard ? t('Card details will be displayed here.') : t('ACH details will be displayed here.')}
            </Text>
          )}
        </>
      )}

      {inlineErrors.length > 0 && (
        <Text id={`${fieldInfo.key}-error`} size='small' color='error' className={labelStyle}>
          {[...new Set(inlineErrors)].join(' ')}
        </Text>
      )}

      {isPreviewMode ? (
        <PaymentPreviewModal modalProps={modalProps} title={fieldInfo.label} type={type} />
      ) : (
        <PaymentMethodSetupModal
          modalProps={{ ...modalProps, onClose: handleClose }}
          title={fieldInfo.label}
          kind={kind}
          api={api}
          gatewayConfig={gatewayConfig}
          setupKey={fieldInfo.key}
          session={setup.session}
          onSaved={handleSaved}
          onEvent={handleEvent}
        />
      )}
    </fieldset>
  );
};

const fieldsetStyle = css`
  border-top: none;
  border-right: none;
  border-bottom: none;
  border-left: none;
  margin-top: 0;
  margin-right: 0;
  margin-bottom: 0;
  margin-left: 0;
  padding-top: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-left: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  row-gap: var(--fabric-space-gap-xs);
  column-gap: var(--fabric-space-gap-xs);
`;

const labelStyle = css`
  margin-top: 0;
  margin-right: 0;
  margin-bottom: 0;
  margin-left: 0;
`;

const loadingRowStyle = css`
  display: flex;
  align-items: center;
  row-gap: var(--fabric-space-gap-xs);
  column-gap: var(--fabric-space-gap-xs);

  p {
    margin-top: 0;
    margin-right: 0;
    margin-bottom: 0;
    margin-left: 0;
  }
`;

const savedStyle = css`
  display: flex;
  flex-direction: column;
  row-gap: var(--fabric-space-gap-sm);
  column-gap: var(--fabric-space-gap-sm);
`;

const savedRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  row-gap: var(--fabric-space-gap-md);
  column-gap: var(--fabric-space-gap-md);
  padding-top: var(--fabric-space-padding-sm);
  padding-right: var(--fabric-space-padding-sm);
  padding-bottom: var(--fabric-space-padding-sm);
  padding-left: var(--fabric-space-padding-sm);
  border-top: 1px solid var(--fabric-color-neutral-20);
  border-right: 1px solid var(--fabric-color-neutral-20);
  border-bottom: 1px solid var(--fabric-color-neutral-20);
  border-left: 1px solid var(--fabric-color-neutral-20);
  border-top-left-radius: var(--fabric-radius-md);
  border-top-right-radius: var(--fabric-radius-md);
  border-bottom-left-radius: var(--fabric-radius-md);
  border-bottom-right-radius: var(--fabric-radius-md);
`;

const addTileStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  row-gap: var(--fabric-space-gap-xs);
  column-gap: var(--fabric-space-gap-xs);
  width: 100%;
  min-height: 72px;
  padding-top: var(--fabric-space-padding-md);
  padding-right: var(--fabric-space-padding-md);
  padding-bottom: var(--fabric-space-padding-md);
  padding-left: var(--fabric-space-padding-md);
  border-top: 1px dashed var(--fabric-color-neutral-30);
  border-right: 1px dashed var(--fabric-color-neutral-30);
  border-bottom: 1px dashed var(--fabric-color-neutral-30);
  border-left: 1px dashed var(--fabric-color-neutral-30);
  border-top-left-radius: var(--fabric-radius-md);
  border-top-right-radius: var(--fabric-radius-md);
  border-bottom-left-radius: var(--fabric-radius-md);
  border-bottom-right-radius: var(--fabric-radius-md);
  color: var(--fabric-color-action-primary-background-default);
  background-color: var(--fabric-color-neutral-5);

  &:hover:not(:disabled),
  &:focus-visible {
    border-top-color: var(--fabric-color-action-primary-background-default);
    border-right-color: var(--fabric-color-action-primary-background-default);
    border-bottom-color: var(--fabric-color-action-primary-background-default);
    border-left-color: var(--fabric-color-action-primary-background-default);
  }
`;

const addTileErrorStyle = css`
  border-top-color: var(--fabric-color-border-error);
  border-right-color: var(--fabric-color-border-error);
  border-bottom-color: var(--fabric-color-border-error);
  border-left-color: var(--fabric-color-border-error);
`;
